import { z } from 'zod'
import { isValidPhone, validateCpf, validateFullName } from '@/helpers'
import {
  COVERAGE_OPTIONS,
  DENTAL_SPECIALTIES,
  MEDICAL_SPECIALTIES,
  OTHER_HEALTH_SPECIALTIES,
  PROFESSIONAL_CATEGORIES,
  specialtiesForCategories,
} from './constants'

const specialtyValues = [
  ...MEDICAL_SPECIALTIES,
  ...DENTAL_SPECIALTIES,
  ...OTHER_HEALTH_SPECIALTIES,
] as unknown as [string, ...string[]]
const categoryValues = PROFESSIONAL_CATEGORIES.map(
  (category) => category.value,
) as unknown as ['medicos', 'odontologia', 'demais_profissionais']
const coverageValues = COVERAGE_OPTIONS.map(
  (option) => option.value,
) as unknown as [number, ...number[]]

export const rcpAddressSchema = z
  .object({
    zip_code: z.string().min(1, 'Obrigatório'),
    street: z.string().min(1, 'Obrigatório'),
    number: z.string(),
    no_number: z.boolean(),
    complement: z.string().optional(),
    district: z.string().min(1, 'Obrigatório'),
    city: z.string().min(1, 'Obrigatório'),
    state_code: z.string().min(1, 'Obrigatório'),
    ibge_code: z.string().min(1, 'Obrigatório'),
  })
  .superRefine((address, ctx) => {
    if (!address.no_number && !address.number.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Obrigatório',
        path: ['number'],
      })
    }
  })

function isEmptyAddress(value: unknown): boolean {
  if (!value || typeof value !== 'object') return true

  const address = value as Record<string, unknown>
  return ![
    address.zip_code,
    address.street,
    address.district,
    address.city,
    address.state_code,
    address.ibge_code,
  ].some((field) => typeof field === 'string' && field.trim().length > 0)
}

export const rcpQuoteFormSchema = z
  .object({
    tax_id: z
      .string()
      .min(1, 'Obrigatório')
      .refine(validateCpf, 'CPF inválido'),
    full_name: z
      .string()
      .min(1, 'Obrigatório')
      .refine(validateFullName, 'Informe o nome completo'),
    has_social_name: z.boolean(),
    social_name: z.string().optional(),
    phone_type: z.enum(['celular', 'comercial', 'residencial'], {
      required_error: 'Obrigatório',
    }),
    phone: z
      .string()
      .min(1, 'Obrigatório')
      .refine(isValidPhone, 'Telefone inválido'),
    insurance_kind: z.enum(['new', 'renewal'], {
      required_error: 'Obrigatório',
    }),
    previous_company_name: z.string().optional(),
    continuity_date: z.string().optional(),
    professional_categories: z
      .array(z.enum(categoryValues))
      .min(1, 'Selecione ao menos uma área'),
    specialties: z.array(z.enum(specialtyValues)),
    had_claims_last_5_years: z.boolean({ required_error: 'Obrigatório' }),
    knows_future_claim_risk: z.boolean({ required_error: 'Obrigatório' }),
    has_active_professional_registration: z.boolean({
      required_error: 'Obrigatório',
    }),
    email: z.string().min(1, 'Obrigatório').email('E-mail inválido'),
    marital_status: z.enum(
      ['solteiro', 'casado', 'viuvo', 'desquitado', 'divorciado'],
      { required_error: 'Obrigatório' },
    ),
    sex: z.enum(['masculino', 'feminino'], { required_error: 'Obrigatório' }),
    is_politically_exposed: z.boolean({ required_error: 'Obrigatório' }),
    lives_in_brazil: z.boolean({ required_error: 'Obrigatório' }),
    coverages: z
      .array(z.number())
      .min(1, 'Selecione ao menos uma cobertura')
      .refine(
        (values) => values.every((value) => coverageValues.includes(value)),
        'Cobertura inválida',
      ),
    residential_address: rcpAddressSchema,
    use_residential_as_billing: z.boolean(),
    billing_address: z.unknown().optional(),
  })
  .superRefine((data, ctx) => {
    const hasMedicalCategory = data.professional_categories.includes('medicos')
    const hasOtherHealthCategory =
      data.professional_categories.includes('demais_profissionais')
    const hasDentalCategory =
      data.professional_categories.includes('odontologia')
    const medicalSpecialties = new Set<string>(MEDICAL_SPECIALTIES)
    const otherHealthSpecialties = new Set<string>(OTHER_HEALTH_SPECIALTIES)
    const dentalSpecialties = new Set<string>(DENTAL_SPECIALTIES)
    const hasMedicalSpecialty = data.specialties.some((specialty) =>
      medicalSpecialties.has(specialty),
    )
    const hasOtherHealthSpecialty = data.specialties.some((specialty) =>
      otherHealthSpecialties.has(specialty),
    )
    const hasDentalSpecialty = data.specialties.some((specialty) =>
      dentalSpecialties.has(specialty),
    )

    if (hasMedicalCategory && !hasMedicalSpecialty) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Selecione ao menos uma especialidade',
        path: ['specialties'],
      })
    }

    if (hasOtherHealthCategory && !hasOtherHealthSpecialty) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Selecione ao menos uma especialidade',
        path: ['specialties'],
      })
    }

    if (hasDentalCategory && !hasDentalSpecialty) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Selecione ao menos uma especialidade de odontologia',
        path: ['specialties'],
      })
    }

    if (data.professional_categories.length > 0) {
      const allowed = new Set(
        specialtiesForCategories(data.professional_categories),
      )

      if (data.specialties.some((specialty) => !allowed.has(specialty))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Especialidade inválida para a área selecionada',
          path: ['specialties'],
        })
      }
    }

    if (data.has_social_name && !data.social_name?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Obrigatório',
        path: ['social_name'],
      })
    }

    if (data.insurance_kind === 'renewal') {
      if (!data.previous_company_name?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Obrigatório',
          path: ['previous_company_name'],
        })
      }

      if (!data.continuity_date?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Obrigatório',
          path: ['continuity_date'],
        })
      } else if (!/^\d{2}\/\d{2}\/\d{4}$/.test(data.continuity_date)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Use o formato dd/mm/aaaa',
          path: ['continuity_date'],
        })
      }
    }

    if (data.use_residential_as_billing) return

    const billing = rcpAddressSchema.safeParse(data.billing_address)
    if (billing.success) return

    if (isEmptyAddress(data.billing_address)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Informe o endereço de cobrança',
        path: ['billing_address'],
      })
      return
    }

    for (const issue of billing.error.issues) {
      ctx.addIssue({
        ...issue,
        path: ['billing_address', ...issue.path],
      })
    }
  })

export type RcpAddressFormData = z.infer<typeof rcpAddressSchema>
export type RcpQuoteFormData = Omit<
  z.infer<typeof rcpQuoteFormSchema>,
  'billing_address'
> & {
  billing_address: RcpAddressFormData
}

export const emptyAddress = {
  zip_code: '',
  street: '',
  number: '',
  no_number: false,
  complement: '',
  district: '',
  city: '',
  state_code: '',
  ibge_code: '',
}

export const rcpQuoteDefaultValues: RcpQuoteFormData = {
  tax_id: '',
  full_name: '',
  has_social_name: false,
  social_name: '',
  phone_type: 'celular',
  phone: '',
  insurance_kind: 'new',
  previous_company_name: '',
  continuity_date: '',
  professional_categories: [],
  specialties: [],
  had_claims_last_5_years: undefined as unknown as boolean,
  knows_future_claim_risk: undefined as unknown as boolean,
  has_active_professional_registration: undefined as unknown as boolean,
  email: '',
  marital_status: 'solteiro',
  sex: 'masculino',
  is_politically_exposed: undefined as unknown as boolean,
  lives_in_brazil: undefined as unknown as boolean,
  coverages: [],
  residential_address: emptyAddress,
  use_residential_as_billing: true,
  billing_address: emptyAddress,
}

export const STEP_FIELDS: Array<Array<keyof RcpQuoteFormData>> = [
  [
    'full_name',
    'has_social_name',
    'social_name',
    'sex',
    'tax_id',
    'phone_type',
    'phone',
    'email',
    'residential_address',
    'use_residential_as_billing',
    'billing_address',
    'marital_status',
    'is_politically_exposed',
    'lives_in_brazil',
  ],
  [
    'professional_categories',
    'specialties',
    'had_claims_last_5_years',
    'knows_future_claim_risk',
    'has_active_professional_registration',
  ],
  ['insurance_kind', 'previous_company_name', 'continuity_date', 'coverages'],
]
